import React, {
    RefObject, useLayoutEffect, useRef, useState,
} from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import classnames from 'classnames/bind';

import COLOR from '@CONSTANTS/COLOR.constant';

import { useDownloadDocument, useGetContent } from '@SERVICES';

import GenerationSteps from '@CONSTANTS/GENERATION_STEPS.constant';

import {
    DocumentStep,
    BrandStep,
    CountryStep,
    TypeStep,
    OptionsStep,
    DownloadDocumentStep,
    DownloadDocumentStepData,
    SelectContentStep,
    RegionStep,
} from '@REDUCERS';
import useDocumentGenerationSteps from '@HOOKS/store/useDocumentGenerationSteps';

import { notify } from '@NOTIFICATION/Notificator';

import { GenerationMode } from '@PAGES/manager/options/Options.page';
import { ContentChapter, ContentSection } from '@INTERFACES/api/content';

import BasicSelect from 'src/view/COMPONENTS/COMMON/inputs/select/inputs/BasicSelect';
import BasicInput from '@COMPONENTS/COMMON/inputs/input/BasicInput';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import RoundIconButton from '@COMPONENTS/COMMON/buttons/RoundIconButton';
import StepNavigation from '@COMPONENTS/COMMON/navigation/StepNavigation';

import { FileText, Copy, Download as DownloadIcon, PartyPopper } from 'lucide-react';

import createDocumentLinkAndClick from '@HELPERS/createDocumentLinkAndClick';
import getOrigin from '@HELPERS/getOrigin';

import styles from './DownloadDocument.module.scss';

const cx: CX = classnames.bind(styles);

export enum Expiration {
    HOURS_24 = 1,
    DAYS_3 = 2,
    DAYS_7 = 3,
    DAYS_14 = 4,
    UNLIMITED = 5,
}

type TimeOption = {
    value: Expiration;

    label: string;
};

const expirationTime = [
    {
        value: Expiration.HOURS_24,
        label: '24 hours',
    },
    {
        value: Expiration.DAYS_3,
        label: '3 days',
    },
    {
        value: Expiration.DAYS_7,
        label: '7 days',
    },
    {
        value: Expiration.DAYS_14,
        label: '14 days',
    },
    {
        value: Expiration.UNLIMITED,
        label: 'Unlimited',
    },
];

const timeMap = {
    [Expiration.HOURS_24]: 24,
    [Expiration.DAYS_3]: 24 * 3,
    [Expiration.DAYS_7]: 24 * 7,
    [Expiration.DAYS_14]: 24 * 14,
    [Expiration.UNLIMITED]: 0,
};

function DownloadDocumentPage() {
    const {
        actions: documentGenerationStepsActions,
        services: documentGenerationStepsServices,
    } = useDocumentGenerationSteps();

    const [
        generate,
        {
            data: downloadDocumentData, isLoading, isSuccess, isError, error,
        },
    ] = useDownloadDocument();

    const documentStep: DocumentStep = documentGenerationStepsServices.getStepData(GenerationSteps.DOCUMENT);
    const typeStep: TypeStep = documentGenerationStepsServices.getStepData(GenerationSteps.TYPE);
    const brandStep: BrandStep = documentGenerationStepsServices.getStepData(GenerationSteps.BRAND);
    const countryStep: CountryStep = documentGenerationStepsServices.getStepData(GenerationSteps.LOCATION);
    const downloadDocumentStep: DownloadDocumentStep = documentGenerationStepsServices.getStepData(GenerationSteps.DOWNLOAD_DOCUMENT);
    const downloadDocumentStepData = downloadDocumentStep.data;

    // Get content data for full extraction mode
    const {
        data: contentData = { data: [], meta: { tags: [] } },
    } = useGetContent({
        document: documentStep.data?.id,
        brand: brandStep.data?.id,
        country: countryStep.data?.id,
        type: typeStep.data?.id,
    });

    const [selectedTime, setSelectedTime] = useState<TimeOption>(
        downloadDocumentStepData?.expirationTime !== undefined
            ? expirationTime.find((t) => t.value === downloadDocumentStepData.expirationTime)!
            : expirationTime[0],
    );

    const [watermarkText, setWatermarkText] = useState<string>(downloadDocumentStepData?.watermarkText || '');

    const [link, setLink] = useState(downloadDocumentStepData?.link || '');

    const linkRef: RefObject<HTMLTextAreaElement> = useRef(null);

    useLayoutEffect(() => {
        if (isSuccess) {
            const { url } = downloadDocumentData!;

            setLink(url);

            const data: DownloadDocumentStepData = {
                expirationTime: selectedTime.value,
                watermarkText,
                link: url,
            };

            documentGenerationStepsActions.apply({
                id: GenerationSteps.DOWNLOAD_DOCUMENT,
                isComplete: true,
                data,
            });
        }
    }, [documentGenerationStepsActions, selectedTime, watermarkText, downloadDocumentData, isSuccess]);

    useLayoutEffect(() => {
        if (downloadDocumentStepData) {
            if (selectedTime.value !== downloadDocumentStepData!.expirationTime) {
                setLink('');
            }
        }
    }, [selectedTime, downloadDocumentStepData]);

    useLayoutEffect(() => {
        if (downloadDocumentStepData) {
            if (watermarkText !== downloadDocumentStepData!.watermarkText) {
                setLink('');
            }
        }
    }, [watermarkText, downloadDocumentStepData]);

    useLayoutEffect(() => {
        if (isError) {
            if ((error as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'download-document-error-permission-error',
                });
            } else {
                notify.error('Something wrong!', {
                    toastId: 'download-document-error',
                });
            }
        }
    }, [isError, error]);

    function getDownloadLink() {
        if (!link) return '';

        const origin = getOrigin();

        return origin + link;
    }

    // Helper function to extract all content IDs for full extraction mode
    function getAllContentIds(chaptersData: ContentChapter[]) {
        const chaptersIds: number[] = [];
        const sectionsIds: number[] = [];
        const variantsIds: number[] = [];

        function extractSectionIds(sections: ContentSection[]) {
            sections.forEach((section) => {
                sectionsIds.push(section.id);
                // Add all variants from this section
                section.variants.forEach((variant) => {
                    variantsIds.push(variant.id);
                });
                // Recursively process child sections
                if (section.children?.length) {
                    extractSectionIds(section.children);
                }
            });
        }

        chaptersData.forEach((chapter) => {
            chaptersIds.push(chapter.id);
            extractSectionIds(chapter.sections);
        });

        return {
            chaptersIds,
            sectionsIds,
            variantsIds,
        };
    }

    function generateDocument() {
        const optionsStep: OptionsStep = documentGenerationStepsServices.getStepData(GenerationSteps.OPTIONS);
        const selectContentStep: SelectContentStep = documentGenerationStepsServices.getStepData(GenerationSteps.SELECT_CONTENT);

        const isPartial = optionsStep!.data.id === GenerationMode.PARTIAL;

        let variantsIds: number[];
        let sectionsIds: number[];
        let chaptersIds: number[];

        if (isPartial) {
            // For partial mode, use selected content from the select content step
            variantsIds = selectContentStep.data?.variantsIds || [];
            sectionsIds = selectContentStep.data?.sectionsIds || [];
            chaptersIds = selectContentStep.data?.chaptersIds || [];
        } else {
            // For full extraction mode, get all content IDs
            const allContentIds = getAllContentIds(contentData.data);
            variantsIds = allContentIds.variantsIds;
            sectionsIds = allContentIds.sectionsIds;
            chaptersIds = allContentIds.chaptersIds;
        }

        generate({
            document: documentStep.data!.id,
            variant: variantsIds,
            section: sectionsIds,
            chapter: chaptersIds,
            expirationTime: timeMap[selectedTime.value],
            propertyName: optionsStep.data?.propertyName,

            brand: brandStep.data?.id,
            country: countryStep.data?.id,
            propertyType: typeStep.data?.id,

            isPartial,

            watermarkText: watermarkText.trim(),
        });
    }

    function downloadDocument() {
        createDocumentLinkAndClick({
            url: link,
            name: link?.split('/')?.pop()?.split('.')?.slice(0, -1)
                ?.join('.')
                ?.toString() || '',
        });
    }

    return (
        <>
            <div className={cx('page')}>
                <div className="container max-constraint center">
                    <div className={cx('content-wrapper')}>

                        <div className={cx('content')}>
                            <div className={cx('header-section')}>
                                <div className={cx('congrats-icon')}>
                                    <PartyPopper size={32} color="white" stroke="white" />
                                </div>
                                <div className={cx('title-wrapper')}>
                                    <h1 className={cx('main-title')}>
                                        Your Document is Ready!
                                    </h1>
                                    <p className={cx('subtitle')}>
                                        Choose your download options below to generate a shareable link or download directly
                                    </p>
                                </div>
                            </div>

                            <div className={cx('select-wrapper')}>
                                <BasicSelect
                                    options={expirationTime}
                                    title="Download Link Expiration Time"
                                    value={selectedTime}
                                    isClearable={false}
                                    onChange={(val) => {
                                        setSelectedTime(val);
                                    }}
                                />
                            </div>
                            <div className={cx('watermark-input')}>
                                <BasicInput
                                    value={watermarkText}
                                    label="Watermark Text (Optional)"
                                    onChange={(val: string) => {
                                        setWatermarkText(val);
                                    }}
                                />
                            </div>

                            {link && (
                                <div className={cx('download-card')}>
                                    <div className={cx('download-card-header')}>
                                        <div className={cx('download-card-icon')}>
                                            <FileText size={24} color="white" />
                                        </div>
                                        <div className={cx('download-card-content')}>
                                            <div className={cx('download-card-title')}>
                                                Your Download Link
                                            </div>
                                            <div className={cx('download-card-subtitle')}>
                                                Copy the link below to share or bookmark your generated document
                                            </div>
                                        </div>
                                    </div>

                                    <div className={cx('download-link-container')}>
                                        <div className={cx('download-link-wrapper')}>
                                            <div className={cx('link-text')}>
                                                {getOrigin() + link}
                                            </div>
                                            <div className={cx('copy-button-wrapper')}>
                                                <RoundIconButton
                                                    tooltipTitle="Copy to clipboard"
                                                    icon="copy-lucide"
                                                    onClick={() => {
                                                        const origin = getOrigin();
                                                        navigator.clipboard?.writeText(origin + link).then(() => {
                                                            notify.success('Copied to clipboard!', {
                                                                toastId: 'copy-download-link',
                                                            });
                                                        }).catch(() => {
                                                            notify.error('Failed to copy to clipboard', {
                                                                toastId: 'copy-download-link-error',
                                                            });
                                                        });
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className={cx('download-actions')}>
                                        <div className={cx('download-info')}>
                                            <span className={cx('expiration-label')}>Expires:</span>
                                            <span className={cx('expiration-time')}>
                                                {selectedTime.label === 'Unlimited' ? 'Never' : `In ${selectedTime.label}`}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className={cx('button-wrapper')}>
                            <BasicButton
                                title={link ? 'Download PDF' : 'Generate PDF'}
                                variant="success"
                                size="large"
                                isProcessing={isLoading}
                                style={{
                                    height: 58,
                                    width: '100%',
                                    fontSize: 18,
                                }}
                                onClick={() => {
                                    if (link) {
                                        downloadDocument();
                                    } else {
                                        generateDocument();
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <StepNavigation
                onNext={() => {}}
                showNext={false}
                sticky
            />
        </>
    );
}

export default DownloadDocumentPage;
